import {getTestProps} from '@benchling/js-test-utils';
import {LegendItem, LegendLabel} from '@visx/legend';
import {FormattedLabel} from '@visx/legend/lib/types';
import {getStringWidth} from '@visx/text';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {LabelCompatibleType} from 'ui-library/charting/Types';

import {LegendItemDirection} from './LegendConstants';
import {DEFAULT_LABEL_STYLE, LEGEND_ITEM_EXCLUDED_COLOR, LEGEND_ITEM_HOVER_BACKGROUND} from './utils/Theme';

export const LEGEND_ITEM_HEIGHT = DEFAULT_LABEL_STYLE.fontSize * DEFAULT_LABEL_STYLE.lineHeight;
const LEGEND_GLYPH_SIZE = 16;
const LEGEND_ITEM_LATERAL_MARGIN = 5;
const LEGEND_LABEL_LEFT_MARGIN = 4;
const VERTICAL_LEGEND_BOTTOM_MARGIN = 10;

export function estimateDiscreteSeriesLegendItemWidth(label: string): number {
  return (
    // extra space buffer to avoid wrapping labels due to underestimation
    (getStringWidth(label, DEFAULT_LABEL_STYLE) ?? 0) +
    LEGEND_LABEL_LEFT_MARGIN +
    LEGEND_GLYPH_SIZE +
    LEGEND_ITEM_LATERAL_MARGIN * 2 +
    10
  );
}

export interface DiscreteSeriesLegendItemProps {
  direction?: LegendItemDirection;
  label: FormattedLabel<LabelCompatibleType, string>;
  isExcluded?: boolean;
  onHover?: (label?: LabelCompatibleType) => void;
  onClickLegendItem?: (label: LabelCompatibleType) => void;
}

export function DiscreteSeriesLegendItem({
  direction = LegendItemDirection.ROW,
  label,
  isExcluded,
  onClickLegendItem,
  onHover,
}: DiscreteSeriesLegendItemProps): JSX.Element {
  const labelKey = String(label.datum);
  const handleOnClick = onClickLegendItem
    ? () => {
        onClickLegendItem(label.datum);
      }
    : undefined;

  return (
    <LegendItem
      {...(onHover
        ? {
            onMouseEnter: () => {
              onHover(label.datum);
            },
            onMouseLeave: () => {
              onHover();
            },
          }
        : {})}
      className={css(
        styles.legendItem,
        onClickLegendItem && styles.clickable,
        isExcluded && styles.excluded,
        direction === LegendItemDirection.COLUMN && styles.verticalLegendItem
      )}
      onClick={handleOnClick}
      {...getTestProps({component: 'DiscreteSeriesLegendItem', element: 'root', key: labelKey})}
      data-amp-safe={false}
    >
      <div
        className={css(styles.legendLabelSwatch)}
        style={{backgroundColor: label.value}}
        {...getTestProps({component: 'DiscreteSeriesLegendItem', element: 'swatch', key: labelKey})}
        data-amp-safe={false}
      />
      <LegendLabel
        className={css(styles.legendLabel)}
        margin={0}
        {...getTestProps({component: 'DiscreteSeriesLegendItem', element: 'label', key: labelKey})}
        data-amp-safe={false}
      >
        {label.text}
      </LegendLabel>
    </LegendItem>
  );
}

export const styles = StyleSheet.create({
  clickable: {
    ':hover': {
      backgroundColor: LEGEND_ITEM_HOVER_BACKGROUND,
      cursor: 'pointer',
    },
  },
  excluded: {
    color: LEGEND_ITEM_EXCLUDED_COLOR,
    textDecoration: 'line-through',
  },
  legendItem: {
    marginLeft: LEGEND_ITEM_LATERAL_MARGIN,
    marginRight: LEGEND_ITEM_LATERAL_MARGIN,
    alignItems: 'flex-start',
  },
  legendLabel: {
    ...DEFAULT_LABEL_STYLE,
    align: 'left',
    marginLeft: LEGEND_LABEL_LEFT_MARGIN,
    maxHeight: 'auto',
    whitespace: 'nowrap',
    wrap: 'nowrap',
    wordBreak: 'break-word',
  },
  legendLabelSwatch: {
    margin: `${(LEGEND_ITEM_HEIGHT - LEGEND_GLYPH_SIZE) * 0.5}px 0px`,
    height: LEGEND_GLYPH_SIZE,
    width: LEGEND_GLYPH_SIZE,
  },
  verticalLegendItem: {
    marginBottom: VERTICAL_LEGEND_BOTTOM_MARGIN,
    height: 'auto',
  },
});
